import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Tabs, Button, message, Space, Row, Col,
  Alert, Select, Empty, Modal, Input, Switch, App, Divider
} from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import {
  LayoutOutlined, BgColorsOutlined, FontSizeOutlined,
  SaveOutlined, EyeOutlined, ReloadOutlined, GlobalOutlined,
  PictureOutlined, LinkOutlined, CodeOutlined, CopyOutlined, ContactsOutlined,
  OrderedListOutlined, VideoCameraOutlined
} from '@ant-design/icons';
import EstructuraTab from './components/EstructuraTab';
import ColoresTab from './components/ColoresTab';
import TipografiasTab from './components/TipografiasTab';
import FondoHeroTab from './components/FondoHeroTab';
import OrdenCategoriasTab from './components/OrdenCategoriasTab';
import ContactoTab from './components/ContactoTab';
import TransmisionTab from './components/TransmisionTab';
import EmbedExternoBanner from './components/EmbedExternoBanner';
import PreviewMicrositio from './components/PreviewMicrositio';
import { eventoService, fixUrl } from '../../services/api';
import { eventosDeListaCorta } from '../../utils/listaCorta';

const { Title, Text } = Typography;
const { Option } = Select;

// Valores por defecto (deben coincidir con backend)
const DEFAULT_CONFIG = {
  cabecera: 'cabecera-1',
  columnas_catalogo: '4',
  filtro_posicion: 'derecha',
  columnas_footer: '3',
  color_primary: '#1a5f2a',
  color_secondary: '#8B4513',
  color_title: '#1a1a1a',
  color_subtitle: '#333333',
  color_paragraph: '#666666',
  color_icon: '#1a5f2a',
  color_icon_hover: '#145022',
  color_cta_bg: '#f5f5f5',
  color_btn_primary: '#1a5f2a',
  color_btn_primary_hover: '#145022',
  color_btn_secondary: '#8B4513',
  color_btn_secondary_hover: '#6d360f',
  tipografia_combo: 'moderna',
  // Incrustación externa (debe coincidir con DEFAULT_MICROSITIO_CONFIG del backend)
  embed_externo: false,
  url_embed_publico: '',
  // Transmisión en vivo del remate físico
  url_streaming: '',
  titulo_streaming: '',
  // Ficha del animal: cómo se leen la genealogía y los DEP's (debe coincidir
  // con DEFAULT_MICROSITIO_CONFIG del backend)
  genealogia_vista: 'arbol',
  deps_vista: 'tabla',
  // "Corral" o "Lote": la palabra con la que el micrositio nombra al grupo
  // que se vende junto (debe coincidir con el backend)
  termino_lote: 'corral',
  // Margen superior del hero en px; null = automático (alto de la cabecera)
  hero_margen_superior: null,
};

// `TABS_FULL_WIDTH` ya no existe (2026-10-14): decía cuáles tabs se mostraban
// SIN el preview al lado, y ahora NINGUNA lo tiene al lado. El preview es un
// modal a pantalla completa y la configuración ocupa todo el ancho siempre, así
// que agregar una tab dejó de exigir acordarse de anotarla en ninguna lista.

const Micrositio = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [restaurando, setRestaurando] = useState(false);
  const [eventos, setEventos] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [eventoCompleto, setEventoCompleto] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  // La última config que vino del servidor (al cargar, guardar o restaurar).
  // Con ella se sabe si hay cambios sin guardar antes de cambiar de remate:
  // hasta el 2026-11-03 el selector los descartaba sin avisar (hallazgo #4).
  const [configGuardada, setConfigGuardada] = useState(DEFAULT_CONFIG);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('estructura');
  const [embedVisible, setEmbedVisible] = useState(false);

  useEffect(() => {
    cargarEventos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarEventos = async () => {
    setLoading(true);
    try {
      // lista-corta: solo 6 campos por evento (era getAll con ~30 campos).
      const response = await eventoService.getListaCorta({ estado: 1 });
      const lista = eventosDeListaCorta(response);
      setEventos(lista);

      // Si hay eventos, seleccionar el primero
      if (lista.length > 0) {
        handleSelectEvento(lista[0].id_evento);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const hayCambiosSinGuardar = JSON.stringify(config) !== JSON.stringify(configGuardada);

  // Si cerrás la pestaña con cambios sin guardar, el navegador pregunta.
  useEffect(() => {
    if (!hayCambiosSinGuardar) return undefined;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [hayCambiosSinGuardar]);

  const handleSelectEvento = async (eventoId) => {
    if (!eventoId) {
      setSelectedEvento(null);
      setEventoCompleto(null);
      setConfig(DEFAULT_CONFIG);
      setConfigGuardada(DEFAULT_CONFIG);
      return;
    }

    try {
      // Paralelizar: antes eran 2 awaits serializados (~2x latencia).
      const [responseMicro, responseEvento] = await Promise.all([
        eventoService.getMicrositioConfig(eventoId),
        eventoService.getById(eventoId),
      ]);

      setSelectedEvento({
        id: eventoId,
        nombre: responseMicro.data.nombre_evento,
      });
      setEventoCompleto(responseEvento.data);
      const cargada = { ...DEFAULT_CONFIG, ...responseMicro.data.configuracion };
      setConfig(cargada);
      setConfigGuardada(cargada);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar configuración del micrositio');
    }
  };

  // El selector: con cambios sin guardar, pregunta antes de descartarlos.
  const handleCambiarEvento = (eventoId) => {
    if (!hayCambiosSinGuardar || eventoId === selectedEvento?.id) {
      handleSelectEvento(eventoId);
      return;
    }
    Modal.confirm({
      title: 'Tenés cambios sin guardar',
      content: `Si cambiás de remate ahora, los cambios que hiciste en el micrositio de «${selectedEvento?.nombre}» se pierden. ¿Querés continuar igual?`,
      okText: 'Descartar cambios y cambiar',
      okButtonProps: { danger: true },
      cancelText: 'Volver y guardar',
      onOk: () => handleSelectEvento(eventoId),
    });
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleImagenHeroCargada = (nuevaUrl) => {
    // Actualizar el evento completo con la nueva imagen
    setEventoCompleto(prev => ({
      ...prev,
      imgsec_evento: nuevaUrl
    }));
  };

  /* LAS IMAGENES DE LA CABECERA Y DE LOS ANIMALES SIN FOTO se suben por
     `PUT /eventos/:id` multipart, que es el unico endpoint que graba archivos
     del evento: `img_evento` es una COLUMNA y `logo_mobile` / `logo_iso` /
     `img_fallback_animal` son claves de `configuracion_micrositio` que ese
     PUT escribe al recibir el archivo. Mismo camino que ya usaba `FondoHeroTab`
     para el fondo del hero.

     DESPUES DE SUBIR SE RELEE EL EVENTO Y SE SINCRONIZA `config`. No es
     cosmetico: el boton Guardar de esta pantalla manda `config` ENTERO al PUT
     de config, que mergea clave por clave. Si `config.logo_iso_url` quedara
     con el valor viejo, el proximo Guardar pisaria el isotipo recien subido
     con el anterior, en silencio. */
  const releerImagenesDelEvento = async () => {
    const { data } = await eventoService.getById(selectedEvento.id);
    setEventoCompleto(data);
    const cfg = data.configuracion_micrositio || {};
    const imagenes = {
      logo_mobile_url: cfg.logo_mobile_url ?? null,
      logo_iso_url: cfg.logo_iso_url ?? null,
      imagen_fallback_animal: cfg.imagen_fallback_animal ?? null,
    };
    setConfig(prev => ({ ...prev, ...imagenes }));
    // Las imágenes ya quedaron guardadas en el servidor: no cuentan como
    // cambio pendiente de «Guardar Cambios».
    setConfigGuardada(prev => ({ ...prev, ...imagenes }));
  };

  const subirImagenEvento = async (file, campo) => {
    const fd = new FormData();
    fd.append(campo, file);
    await eventoService.update(selectedEvento.id, fd);
    await releerImagenesDelEvento();
  };

  const quitarImagenEvento = async (campo) => {
    const fd = new FormData();
    fd.append(`${campo}_remove`, '1');
    await eventoService.update(selectedEvento.id, fd);
    await releerImagenesDelEvento();
  };

  const handleGuardar = async () => {
    if (!selectedEvento) {
      message.warning('Selecciona un evento primero');
      return;
    }
    
    setSaving(true);
    try {
      await eventoService.updateMicrositioConfig(selectedEvento.id, config);
      setConfigGuardada(config);
      message.success('Configuración del micrositio guardada');
    } catch (error) {
      console.error('Error al guardar:', error);
      message.error(error.response?.data?.error || 'Error al guardar la configuración');
    } finally {
      // En `finally` y no después del try: hoy no cuelga porque el catch se
      // traga todo, pero el día que alguien meta un throw o un navigate ahí,
      // el botón queda girando para siempre.
      setSaving(false);
    }
  };

  // Restaurar vuelve TODA la configuración del micrositio a la de fábrica, EN
  // EL SERVIDOR y sin vuelta atrás. Hasta el 2026-11-03 lo hacía con un click,
  // sin preguntar (hallazgo #3). Ahora pide confirmación y dice qué se pierde.
  const handleRestaurar = () => {
    if (!selectedEvento) {
      message.warning('Selecciona un evento primero');
      return;
    }
    Modal.confirm({
      title: `Restaurar el micrositio de «${selectedEvento.nombre}»`,
      width: 560,
      content: (
        <div>
          <p>
            Vuelve <strong>toda</strong> la configuración de este micrositio a los valores de fábrica,
            directamente en el servidor: tipo de cabecera, colores, tipografías, fondo del hero,
            orden y alias de categorías, textos de bienvenida, redes, contacto, transmisión en vivo,
            y las referencias al logo para celular, al isotipo y a la imagen de los animales sin foto.
          </p>
          <p style={{ marginBottom: 0 }}>
            <strong>No se puede deshacer</strong>: lo que el sitio publicado muestra hoy cambia en el acto.
            Los animales, las fotos y los datos del remate no se tocan.
          </p>
        </div>
      ),
      okText: 'Sí, restaurar todo',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: restaurarEnServidor,
    });
  };

  const restaurarEnServidor = async () => {
    setRestaurando(true);
    try {
      const response = await eventoService.resetMicrositioConfig(selectedEvento.id);
      const restaurada = { ...DEFAULT_CONFIG, ...response.data.configuracion };
      setConfig(restaurada);
      setConfigGuardada(restaurada);
      message.info('Configuración restaurada a valores por defecto');
    } catch (error) {
      // El reset local se hace igual —es útil— pero NO se anuncia como
      // "restaurada": el servidor no restauró nada. Con el mensaje anterior el
      // productor se iba tranquilo sin apretar Guardar y la config quedaba
      // como estaba.
      setConfig(DEFAULT_CONFIG);
      const detalle = error.response?.data?.error;
      message.warning(
        `No se pudo restaurar en el servidor${detalle ? ` (${detalle})` : ''}. `
        + 'Se cargaron los valores por defecto en pantalla: apretá "Guardar Cambios" para confirmarlos.'
      );
    } finally {
      setRestaurando(false);
    }
  };

  const tabItems = [
    {
      key: 'estructura',
      label: (
        <span>
          <LayoutOutlined />
           &nbsp; Estructura
        </span>
      ),
      children: (
        <EstructuraTab
          config={config}
          onChange={handleConfigChange}
          imagenes={{
            logo: eventoCompleto?.img_evento,
            mobile: config.logo_mobile_url,
            iso: config.logo_iso_url,
            fallback: config.imagen_fallback_animal,
          }}
          onSubirImagen={selectedEvento ? subirImagenEvento : undefined}
          onQuitarImagen={selectedEvento ? quitarImagenEvento : undefined}
        />
      ),
    },
    {
      key: 'colores',
      label: (
        <span>
          <BgColorsOutlined />
           &nbsp; Colores
        </span>
      ),
      children: (
        <ColoresTab 
          config={config} 
          onChange={handleConfigChange} 
        />
      ),
    },
    {
      key: 'tipografias',
      label: (
        <span>
          <FontSizeOutlined />
           &nbsp; Tipografías
        </span>
      ),
      children: (
        <TipografiasTab 
          config={config} 
          onChange={handleConfigChange} 
        />
      ),
    },
    {
      key: 'fondo-hero',
      label: (
        <span>
          <PictureOutlined />
           &nbsp; Fondo Hero Zone
        </span>
      ),
      children: selectedEvento ? (
        <FondoHeroTab
          eventoId={selectedEvento.id}
          imagenActual={eventoCompleto?.imgsec_evento}
          onImagenCargada={handleImagenHeroCargada}
          config={config}
          onChange={handleConfigChange}
        />
      ) : null,
    },
    {
      key: 'orden-categorias',
      label: (
        <span>
          <OrderedListOutlined />
           &nbsp; Orden Categorías
        </span>
      ),
      children: selectedEvento ? (
        <OrdenCategoriasTab
          eventoId={selectedEvento.id}
          config={config}
          onChange={handleConfigChange}
        />
      ) : null,
    },
    {
      key: 'contacto',
      label: (
        <span>
          <ContactsOutlined />
           &nbsp; Página Contacto
        </span>
      ),
      children: (
        <ContactoTab
          config={config}
          onChange={handleConfigChange}
        />
      ),
    },
    {
      key: 'transmision',
      label: (
        <span>
          <VideoCameraOutlined />
           &nbsp; Transmisión en Vivo
        </span>
      ),
      children: (
        <TransmisionTab
          config={config}
          onChange={handleConfigChange}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <PujaSpinner size="lg" label="Cargando..." />
    );
  }

  if (eventos.length === 0) {
    return (
      <div>
        <Title level={2}>
          <GlobalOutlined /> Micrositio
        </Title>
        <Card>
          <Empty
            description={
              <span>
                No tienes eventos activos.<br />
                Crea un evento primero para configurar su micrositio.
              </span>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="micrositio-page">
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space size="large" align="center" wrap>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                <GlobalOutlined /> Micrositio
              </Title>
              <Text type="secondary">
                Personaliza el diseño del micrositio de tu evento
              </Text>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 14px',
                background: '#fafaf7',
                border: '1px solid #e8e2d0',
                borderRadius: 8,
              }}
            >
              <Switch
                checked={!!config.embed_externo}
                onChange={(checked) => handleConfigChange('embed_externo', checked)}
                checkedChildren="Sí"
                unCheckedChildren="No"
                disabled={!selectedEvento}
              />
              <Text strong style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                ¿Vas a incrustar este micrositio en tu propio sitio web?
              </Text>
              <Text type="secondary" style={{ fontSize: 13, opacity: 0.4 }}>
                |
              </Text>
              <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                {config.embed_externo
                  ? 'Los links apuntan a tu dominio'
                  : 'Los links apuntan al dominio de PUJA'}
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space>
            <Select
              style={{ width: 300 }}
              placeholder="Seleccionar evento"
              value={selectedEvento?.id}
              onChange={handleCambiarEvento}
            >
              {eventos.map(evento => (
                <Option key={evento.id_evento} value={evento.id_evento}>
                  {evento.nombre_evento}
                </Option>
              ))}
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRestaurar}
              loading={restaurando}
              disabled={!selectedEvento}
            >
              Restaurar
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => setPreviewVisible(true)}
              disabled={!selectedEvento}
            >
              Vista Previa
            </Button>
            <Button
              icon={<LinkOutlined />}
              href={eventoCompleto?.slug ? `/remate/${eventoCompleto.slug}` : undefined}
              target="_blank"
              disabled={!eventoCompleto?.slug}
            >
              Ir al sitio
            </Button>
            <Button
              icon={<CodeOutlined />}
              disabled={!eventoCompleto?.slug}
              onClick={() => setEmbedVisible(true)}
            >
              Código de incrustación
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleGuardar}
              loading={saving}
              disabled={!selectedEvento}
            >
              Guardar Cambios
            </Button>
          </Space>
        </Col>
      </Row>

      {selectedEvento && (
        <Alert
          message={`Editando micrositio de: ${selectedEvento.nombre}`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedEvento && config.embed_externo && (
        <EmbedExternoBanner config={config} onChange={handleConfigChange} />
      )}

      {/* Panel de configuración A TODO EL ANCHO.
          El preview en vivo vivía acá al lado, en 14/16 columnas, y dejaba la
          configuración en un tercio de la pantalla: con el Tabs vertical
          comiéndose su parte, los controles quedaban en una franja tan angosta
          que "Tipo de Cabecera" se partía en dos renglones y cada opción de
          cabecera en cuatro (decisión del usuario, 2026-10-14). El preview no
          se perdió: es el mismo modal a pantalla completa que ya abría el botón
          "Pantalla completa" de esa card, y que sigue abriendo el botón
          "Vista Previa" — ahora en la barra de arriba Y en el encabezado de
          esta card, para tenerlo a mano sin volver al tope de la página. */}
      <Card
        title="Configuración"
        styles={{ body: { padding: '12px' } }}
        extra={
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => setPreviewVisible(true)}
            disabled={!selectedEvento}
          >
            Vista previa
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabPosition="left"
          style={{ minHeight: 500 }}
        />
      </Card>

      {/* Modal de preview completo */}
      <PreviewMicrositio 
        config={config} 
        evento={selectedEvento}
        fondoHero={eventoCompleto?.imgsec_evento}
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
      />

      {/* Modal código de incrustación */}
      <EmbedModal
        visible={embedVisible}
        onClose={() => setEmbedVisible(false)}
        slug={eventoCompleto?.slug}
        nombreEvento={eventoCompleto?.nombre_evento}
        isoUrl={config.logo_iso_url}
      />

      <style>{`
        .micrositio-page .ant-tabs-left > .ant-tabs-nav .ant-tabs-tab {
          padding: 12px 16px;
          margin: 4px 0;
        }
        .micrositio-page .ant-tabs-left > .ant-tabs-content-holder {
          border-left: 1px solid #f0f0f0;
          padding-left: 16px;
        }
        /* La regla sticky de .config-panel se fue con el preview al lado:
           existía para que la configuración quedara a la vista mientras se
           scrolleaba el preview. Sin nada al lado, pegar la card al tope solo
           podía tapar contenido. (Ojo: acá adentro no van comillas invertidas,
           esto es un template literal y cierran el bloque.) */
      `}</style>
    </div>
  );
};

// ── Modal de código de incrustación ──────────────────────────────────
const EmbedModal = ({ visible, onClose, slug, nombreEvento, isoUrl }) => {
  const { message } = App.useApp();

  const origin = window.location.origin;
  const url = `${origin}/remate/${slug}`;

  // El `title` del iframe es lo que leen los lectores de pantalla al llegar al
  // marco, y en la variante "documento completo" es ademas el <title> de la
  // pestaña. "Remate - PUJA" no decia CUAL remate: con tres cabañas publicando
  // a la vez, no distingue nada.
  //
  // `nombre_evento`, NO `nom_evento`: el campo del modelo se llama asi y es un
  // error que ya esta anotado en AGENTS.md porque se repite.
  //
  // Se escapa porque el nombre lo escribe el productor y termina DENTRO de un
  // atributo HTML: unas comillas en 'Remate "El Ombu"' cierran el title antes
  // de tiempo y el snippet sale roto, sin ningun error a la vista. El & va
  // primero o se escaparian los & de los escapes anteriores.
  const escaparHtml = (t) => String(t)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const tituloEmbed = `${escaparHtml((nombreEvento || 'Remate').trim())} | PUJA Gestión de cabañas`;

  // Snippet unificado: iframe + script con auto-resize Y deep-link.
  // El script lee ?animal=X del URL del parent y lo pasa al iframe via
  // postMessage. RematePage escucha el postMessage y navega al animal
  // especifico. Si no hay ?animal= en la URL, no hace nada.
  const iframeId = `puja-iframe-${slug}`;
  // Dos detalles del `style` del iframe que parecen de mas y no lo son:
  //
  // `display:block` — un <iframe> es INLINE por defecto, asi que se apoya en la
  // linea de base del texto y deja abajo el espacio de los trazos descendentes:
  // esa rayita de 2-3px entre el iframe y su contenedor que no se encuentra por
  // ningun lado. En block no hay linea de base y el hueco desaparece.
  //
  // `border:0` — se mantiene aunque no se vea ningun borde. Sacarlo NO deja el
  // iframe sin borde: trae de vuelta el `border: 2px inset` que el navegador le
  // pone por defecto a los iframes.
  const embedCode = `<!-- PUJA · Codigo de incrustacion del remate ${slug} -->
<iframe
  id="${iframeId}"
  src="${url}"
  width="100%"
  height="800"
  style="display:block; border:0;"
  allowfullscreen
  title="${tituloEmbed}"
></iframe>

<script>
(function () {
  var iframe = document.getElementById('${iframeId}');
  if (!iframe) return;

  // Ajuste automatico de altura del iframe + handshake del deep-link
  window.addEventListener('message', function (e) {
    // DE QUIEN VIENE. 'message' en window escucha a TODA la pagina: sin esta
    // linea cualquier script del sitio -un widget de chat, un anuncio, otro
    // embed con auto-resize, que postean {height: N} como payload estandar-
    // redimensiona ESTE iframe, y un 'micrositio-ready' ajeno dispara el
    // reenvio del deep-link. La guarda anterior miraba iframe.src, que es
    // constante y siempre daba true: no discriminaba nada.
    if (e.source !== iframe.contentWindow) return;

    // Y que sea un numero de verdad, porque entra directo en un style.
    if (e.data && typeof e.data.height === 'number' && isFinite(e.data.height)) {
      iframe.style.height = e.data.height + 'px';
      // Casi siempre esto NO va a mover nada, y esta bien que sea asi: mientras
      // el iframe sea mas alto que lo que queda de pantalla -o sea, siempre,
      // que es para lo que existe el auto-alto- el centro visible se calcula
      // con innerHeight y r.top, y ninguno depende de r.height. Se deja para el
      // caso contrario: cuando el contenido se achica tanto que el iframe entra
      // entero en pantalla, ahi si el centro cambia.
      agendarViewport();
    }
    // El micrositio avisa que ya monto su listener -> reenviamos el deep-link
    // (?animal). Evita el race donde el postMessage del load se perdia.
    if (e.data && e.data.type === 'micrositio-ready') {
      syncDeepLink();
      // RESET OBLIGATORIO ANTES DE REAVISAR. El micrositio manda 'ready' cada
      // vez que remonta su listener, y eso pasa en cada navegacion DENTRO del
      // iframe: al pasar de un animal al siguiente, la ficha nueva arranca sin
      // saber donde esta parado el usuario. Si no se limpia el ultimo valor, el
      // dedupe de avisarViewport() se come este reenvio -el scroll no se movio-
      // y la ficha nueva se queda sin dato: sus flechas vuelven a caer en el
      // fallback 'fixed', que es justo lo que este mensaje viene a evitar.
      ultimoCentro = null;
      avisarViewport();
    }
  });

  // Le contamos al micrositio que franja de si mismo estas viendo, para que sus
  // controles flotantes -las flechas para pasar de un animal al siguiente-
  // puedan acompanarte al scrollear. Sin esto quedan clavados: adentro de un
  // iframe que crece hasta entrar entero, 'position: fixed' se fija al viewport
  // DEL IFRAME, y ese viewport mide lo mismo que todo el contenido.
  var pedido = null;
  var ultimoCentro = null;

  function avisarViewport() {
    pedido = null;
    if (!iframe.contentWindow) return;
    var r = iframe.getBoundingClientRect();
    // Franja del iframe que cae dentro de la ventana, en coordenadas del
    // iframe. Se manda el CENTRO ya calculado: aca esta todo lo necesario para
    // resolver los bordes -arriba de todo, abajo de todo, iframe entrando a
    // medias- y del otro lado no hay que reconstruir ningun offset.
    var arriba = Math.max(0, -r.top);
    var abajo = Math.min(r.height, window.innerHeight - r.top);
    if (abajo <= arriba) return; // el iframe no se ve: no hay nada que mover
    var centro = Math.round((arriba + abajo) / 2);
    // Solo si de verdad cambio. Scrollear el contenedor de al lado, o cualquier
    // cosa que dispare scroll sin mover el iframe, no tiene por que costar un
    // postMessage por frame. Mismo criterio que el de la altura en
    // useAvisarAltoAlPadre. Va ahora: este script queda pegado en el sitio del
    // productor y no se puede corregir despues sin que lo re-peguen.
    if (centro === ultimoCentro) return;
    ultimoCentro = centro;
    iframe.contentWindow.postMessage({ type: 'puja-viewport', centro: centro }, '${origin}');
  }

  function agendarViewport() {
    // Un aviso por frame como maximo. El scroll dispara decenas de veces por
    // segundo y postear en cada una no mejora nada.
    if (pedido === null) pedido = requestAnimationFrame(avisarViewport);
  }

  // 'capture: true' NO es decorativo: el evento 'scroll' de un elemento no
  // burbujea, asi que un listener comun en window se pierde el scroll de los
  // page builders de WordPress y Wix, que scrollean un contenedor interno en
  // vez de la pagina. En fase de captura el evento SI pasa por window camino al
  // target, y ahi lo agarramos. Sin esto, en esos sitios la flecha se
  // posicionaria una sola vez al cargar y despues quedaria congelada.
  // Va acá y no en un parche posterior porque el snippet queda pegado en el
  // sitio del productor: cambiarlo despues obliga a que lo re-peguen de nuevo.
  window.addEventListener('scroll', agendarViewport, { passive: true, capture: true });
  window.addEventListener('resize', agendarViewport);
  iframe.addEventListener('load', agendarViewport);

  // Deep-link: si la URL del parent trae ?animal=X o ?vivo=1, navegar ahi
  // dentro del iframe. Solo aplica cuando configuraste 'Incrustacion
  // externa' en el editor del micrositio.
  function syncDeepLink() {
    if (!iframe.contentWindow) return;
    var params = new URLSearchParams(window.location.search);

    var animal = params.get('animal');
    if (animal) {
      iframe.contentWindow.postMessage(
        { type: 'navigate-animal', animal: animal },
        '${origin}'
      );
      return;
    }

    // ?vivo=1 -> transmision en vivo del remate
    if (params.get('vivo')) {
      iframe.contentWindow.postMessage({ type: 'navigate-vivo' }, '${origin}');
    }
  }

  iframe.addEventListener('load', syncDeepLink);
  window.addEventListener('popstate', syncDeepLink); // back/forward del browser
})();
</script>`;

  // EL FAVICON VA ACA Y NO EN EL SNIPPET. La pestaña es de la página de
  // AFUERA: el micrositio, desde el iframe, no la puede tocar. El <script> del
  // snippet sí podría, pero cambiarlo obliga a que todos lo re-peguen y le
  // pisaría el ícono a un sitio que ya tiene el suyo. En la "página completa"
  // el <head> es nuestro, así que va acá, y sólo si el productor cargó un iso.
  // URL ABSOLUTA del archivo de hoy: si después cambia el iso, hay que volver
  // a copiar la página (2026-10-14; las URLs fijas quedaron para cuando se
  // sumen los Open Graph).
  const lineaFavicon = isoUrl
    ? `\n  <link rel="icon" href="${escaparHtml(fixUrl(isoUrl))}">`
    : '';

  // Variante "documento completo" para quien arranca de un archivo/página
  // nueva: incluye la estructura HTML + reset del margin del body (el marco
  // blanco que el navegador agrega por defecto). Copia-pega-y-anda.
  const embedCodeCompleto = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${tituloEmbed}</title>${lineaFavicon}
  <style>
    html, body { margin: 0; padding: 0; }
  </style>
</head>
<body>
${embedCode}
</body>
</html>`;

  const copiar = (texto) => {
    navigator.clipboard.writeText(texto)
      .then(() => message.success('Código copiado al portapapeles'))
      .catch(() => message.error('Seleccioná el código manualmente y copialo.'));
  };

  return (
    <Modal
      title={<span><CodeOutlined style={{ marginRight: 8 }} />Código de incrustación</span>}
      open={visible}
      onCancel={onClose}
      footer={[<Button key="cerrar" onClick={onClose}>Cerrar</Button>]}
      width={680}
    >
      {/* Variante 1: incrustar dentro de una página que YA tiene estructura HTML */}
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 14 }}>1) Para una página existente</Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          Si ya tenés un sitio armado (WordPress, Wix, tu propia web, etc.), pegá este código
          en el HTML de la página donde quieras mostrar el remate.
        </Text>
      </div>
      <Input.TextArea
        value={embedCode}
        autoSize={{ minRows: 10, maxRows: 16 }}
        readOnly
        style={{ fontFamily: 'monospace', fontSize: 12, background: '#f8f8f8' }}
        onClick={(e) => e.target.select()}
      />
      <Button
        icon={<CopyOutlined />}
        onClick={() => copiar(embedCode)}
        style={{ marginTop: 8 }}
        block
      >
        Copiar código para página existente
      </Button>

      <Divider />

      {/* Variante 2: documento HTML completo (archivo/página nueva, sin marco) */}
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 14 }}>2) Para un archivo o página nueva</Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          Si arrancás de cero (un archivo .html vacío), usá este código: trae la estructura
          completa y elimina el marco blanco que agrega el navegador por defecto.
        </Text>
      </div>
      <Input.TextArea
        value={embedCodeCompleto}
        autoSize={{ minRows: 10, maxRows: 18 }}
        readOnly
        style={{ fontFamily: 'monospace', fontSize: 12, background: '#f8f8f8' }}
        onClick={(e) => e.target.select()}
      />
      <Button
        type="primary"
        icon={<CopyOutlined />}
        onClick={() => copiar(embedCodeCompleto)}
        style={{ marginTop: 8 }}
        block
      >
        Copiar página completa
      </Button>

      <div style={{ marginTop: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          URL directa: <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
        </Text>
      </div>
    </Modal>
  );
};


export default Micrositio;
